/**
 * ConversationLineItem.js
 * Talkroute
 *
 * Created by Vel Genov on 3/15/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Grid } from "semantic-ui-react";
import { push } from "connected-react-router";
import ReadIndicator from "../voicemailComponents/ReadIndicator";
import uiString from "../../constants/uiString";
import routes, { routeUrlBuilders } from "../../constants/routes";
import { NEW_CONVERSATION_ID } from "../../services/dataClasses/ConversationData";
import { 
  deleteConversation, 
  MULTIPLE_SELECTION_TYPES, 
  selectConversation, 
  selectMultipleConversation, 
  setTemporaryConversation, 
  switchConversationSelectionMode 
} from "@actions/conversationActions";
import { Dialog } from "@material-ui/core";
import ConfirmDialog from "../PresentationComponents/ConfirmDialog";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { WELCOME_VOICEMAIL } from "@services/dataClasses";
import { useHistory } from "react-router-dom";

const hoverOnStyle = { opacity: "0.6", backgroundColor: "var(--color-white)" };
const hoverOffStyle = { opacity: "1.0", backgroundColor: "var(--color-white)" };
const selectedStyle = { backgroundColor: "var(--color-light-grey)" };
const multipleSelectionStyle = {
  backgroundColor: "var(--color-very-light-grey)",
};
const removeVerticalPadding = { paddingTop: "0px", paddingBottom: "0px" };
const overflowStyle = {
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden",
};

export interface IConversationLineItemProps {
  conversationId: string;
  contactNumber: string;
  lastMessageRead?: boolean;
  lastMessageDate?: string;
  lastMessageBody?: string;
  markConversationAsReadCallback?: ({
    conversationId,
    readStatus
  } : {
    conversationId: string,
    readStatus: boolean,
  }) => any;
}

/**
 * A comoponent that displays a single conversation line item with the passed in props.
 * This will usually be displayead as a part of a collection
 */
const ConversationLineItem = ({
  conversationId,
  contactNumber,
  lastMessageRead,
  lastMessageDate,
  lastMessageBody,
  markConversationAsReadCallback,
}: IConversationLineItemProps) => {
  const [hover, setHover] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const selectedConversationId = useAppSelector(
    state => state.conversations.selectedConversationId
  );
  const multipleSelectedConversations = useAppSelector(
    state => state.conversations.multipleSelectedConversations
  );
  const multipleSelectionMode = useAppSelector(
    state => state.conversations.multipleSelectionMode
  );
  const dispatch = useDispatch();
  const history = useHistory();

  /**
   * Mark conversations as read only
   *
   * Keep the naming and structure around for when we need to toggle read/unread
   */
  const toggleReadIndicator = () => {
    if (markConversationAsReadCallback) {
      markConversationAsRead();
    }
  };

  /**
   * Mark the conversation as read
   *
   * @param isRead is an optional boolean parameter that indicates wheather
   * the conversation is read or unread. Read by default
   */
  const markConversationAsRead = (isRead = true) => {
    // Currently only mark as read
    if (isRead !== lastMessageRead && isRead === true) {
      // Create an update
      if (markConversationAsReadCallback)
        markConversationAsReadCallback({ conversationId, readStatus: isRead });
    }
  };

  /**
   * Called when the user is hovering over a ConversationLineItem
   */
  const onHover = () => setHover(true);

  /**
   * Called when the user is moving out of a ConversationLineItem
   */
  const hoverOff = () => setHover(false);

  /**
   * Delete this conversation
   */
  const deleteConversationHandler = () => {
    setConversationToDelete(null);
    dispatch(deleteConversation(""));
  };

  /**
   * Cancel deleting this conversation
   */
  const cancelDeletingConversation = () => {
    setConversationToDelete(null);
  };

  /**
   * Hide the details display
   */
  const hideDetails = () => {
    dispatch(push(routeUrlBuilders.OPEN_CONVERSATION(conversationId)));
  };

  /**
   * Called when a conversation has been selected
   * Updates the details display, if it's visible
   * Fires the "selectConversation" callback
   *
   * @param conversationId the ID of the selected conversation
   */
  const onConversationSelected = (
    event: MouseEvent,
    conversationId: string
  ) => {
    const LEFT_MOUSE_BUTTON_CODE = 0;
    const RIGHT_MOUSE_BUTTON_CODE = 2;
    if (event.button === LEFT_MOUSE_BUTTON_CODE) { // left mouse click case
      if (event.shiftKey) { // shift + left mouse click
        onSwitchConversationSelectionMode(true);
        onMultipleSelected(conversationId, MULTIPLE_SELECTION_TYPES.RANGE);
      } else if (event.ctrlKey) { // ctrl + left mouse click
        onSwitchConversationSelectionMode(true);
        onMultipleSelected(conversationId, MULTIPLE_SELECTION_TYPES.SINGLE);
      } else { // left mouse click only
        if (multipleSelectionMode) {
          dispatch(switchConversationSelectionMode(false));
          dispatch(selectMultipleConversation("", MULTIPLE_SELECTION_TYPES.NONE));
        }
      }
      if (conversationId !== selectedConversationId) {
        history.push(routeUrlBuilders.OPEN_CONVERSATION(conversationId));
        dispatch(selectConversation(conversationId));
        dispatch(setTemporaryConversation(null, false));
      }
      hideDetails();
    } else if (event.button === RIGHT_MOUSE_BUTTON_CODE) { // right mouse click case

      if (multipleSelectionMode && !multipleSelectedConversations.includes(conversationId)) {
        dispatch(switchConversationSelectionMode(false));
        dispatch(selectMultipleConversation("", MULTIPLE_SELECTION_TYPES.NONE));
      }
      history.push(routeUrlBuilders.OPEN_CONVERSATION(conversationId));
      dispatch(selectConversation(conversationId));
      // dispatch(setTemporaryConversation(null, false));
      hideDetails();
    }
  };

  /**
   * Called when a conversation has been selected in multiple mode
   * Fires the "selectMultipleConversation" callback
   *
   * @param conversationId the ID of the selected conversation
   */
  const onMultipleSelected = (conversationId: string, type: string) =>
    dispatch(selectMultipleConversation(conversationId, type));

  /**
   * Called when a the conversation selection mode is changed
   *
   * @param selectionMode the ID of the selected conversation
   */
  const onSwitchConversationSelectionMode = (selectionMode: boolean) =>
    dispatch(switchConversationSelectionMode(selectionMode));
  const selected = selectedConversationId === conversationId;
  const formattedContactNumber =
    conversationId === NEW_CONVERSATION_ID
      ? uiString.NEW_CONVERSATION
      : contactNumber;

  // Determine the rendering style based on hover status
  const hoverStyle = hover ? hoverOnStyle : hoverOffStyle;
  const gridStyle = selected && !multipleSelectionMode
    ? selectedStyle
    : multipleSelectedConversations?.includes(conversationId)
    ? multipleSelectionStyle
    : hoverStyle;
  const isMessageRead = selected || lastMessageRead;
  const selectedConversationCount = multipleSelectedConversations?.filter((i) => !!i).length;

  return (
    <>
      <Grid
        onMouseEnter={() => onHover()}
        onMouseLeave={() => hoverOff()}
        style={{
          ...gridStyle,
          cursor: "pointer",
          margin: "0px",
          borderBottom: "1px solid var(--color-dark-grey)",
          userSelect: "none"
        }}
        onClick={(event: MouseEvent) =>
          onConversationSelected(event, conversationId)
        }
        onContextMenu={(event: MouseEvent) =>
          onConversationSelected(event, conversationId)
        }
      >
        <Grid.Column
          width={2}
          textAlign="center"
          style={{ ...removeVerticalPadding }}
        >
          <div style={{ paddingTop: "10px" }}>
            <ReadIndicator
              messageRead={isMessageRead ?? true}
              toggleCallBack={toggleReadIndicator}
              allowHover={false}
            />
          </div>
        </Grid.Column>
        <Grid.Column width={9}>
          {/* This section shows 2 rows
            Row 1 - the contactNumber
            Row 2 - lastMessageBody */}
          <Grid.Row className="text-normal">{formattedContactNumber}</Grid.Row>
          <Grid.Row
            className="text-small-light"
            style={{
              ...overflowStyle,
              lineHeight: "1em",
              minHeight: "1em",
            }}
          >
            {lastMessageBody}
          </Grid.Row>
        </Grid.Column>
        <Grid.Column
          width={5}
          textAlign="right"
          style={{
            paddingTop: "20px",
            paddingBottom: "20px",
          }}
          className="text-small"
        >
          {lastMessageDate}
        </Grid.Column>
      </Grid>
      <ConfirmDialog
        header={`${uiString.CONVERSATION_DELETE_CONFIRMATION_HEADER}${ selectedConversationCount <= 1 && ' - ' + contactNumber}`}
        content={selectedConversationCount > 1 ? uiString.MULTIPLE_CONVERSATIONS_DELETE_CONFIRMATION_BODY : uiString.CONVERSATION_DELETE_CONFIRMATION_BODY}
        open={conversationToDelete !== null}
        confirmButtonLabel={uiString.DELETE}
        onCancel={cancelDeletingConversation}
        onConfirm={deleteConversationHandler}
      />
      <Dialog 
      open={conversationToDelete !== null}>

      </Dialog>
    </>
  );
};

export default ConversationLineItem;
